import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import visitService from '../../services/visitService';
import { Modal } from '../Common/Modal';
import '../Common/ElectronicSign.css';
import './VisitEmailAction.css';

/**
 * Page for email Approve/Reject links: /visit/:id/approve and /visit/:id/reject.
 * No login required: user opens link, enters password, visit is approved or rejected.
 * Works both standalone (from email, no session) and inside the app (logged in).
 */
export default function VisitEmailAction({ action = 'approve', flow = 'visit' }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isApprove = action === 'approve';
  const isFollowUp = String(flow || 'visit').toLowerCase() === 'followup';

  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [remark, setRemark] = useState('');
  const [remarkError, setRemarkError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(true);

  useEffect(() => {
    if (token) setModalOpen(true);
  }, [token]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setUsernameError('');
    setPasswordError('');
    setRemarkError('');
    if (!username.trim()) {
      setUsernameError('Username is required.');
      return;
    }
    if (!password.trim()) {
      setPasswordError('Password is required.');
      return;
    }
    if (!remark.trim()) {
      setRemarkError('Remarks are required.');
      return;
    }
    setLoading(true);
    try {
      if (isFollowUp) {
        if (isApprove) {
          await visitService.followUpConfirmByToken(id, token, username.trim(), password, remark.trim() || undefined);
          setSuccessMessage('Follow-up letter approved successfully.');
        } else {
          await visitService.followUpRejectByToken(id, token, username.trim(), password, remark.trim() || undefined);
          setSuccessMessage('Follow-up letter rejected successfully.');
        }
      } else if (isApprove) {
        await visitService.confirmByToken(id, token, username.trim(), password, remark.trim() || undefined);
        setSuccessMessage('Visit approved successfully.');
      } else {
        await visitService.cancelByToken(id, token, username.trim(), password, remark.trim() || undefined);
        setSuccessMessage('Visit rejected successfully.');
      }
      setModalOpen(false);
      setUsername('');
      setPassword('');
      setRemark('');
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Request failed. Please check the token and password.';
      setPasswordError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsernameError('');
    setPasswordError('');
    setRemarkError('');
    setUsername('');
    setPassword('');
    setRemark('');
    setModalOpen(false);
    navigate('/dashboard', { replace: true });
  };

  if (!token) {
    return (
      <div className="visit-email-action-standalone content-area p-4">
        <div className="alert alert-danger">
          Invalid or missing link. The approval link may have expired or already been used.
        </div>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/dashboard', { replace: true })}>
          Go to Dashboard
        </button>
      </div>
    );
  }

  const footer = (
    <div className="electronic-sign-footer">
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? 'Submitting…' : isApprove ? 'Approve Visit' : 'Reject Visit'}
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClose}>
        Cancel
      </button>
    </div>
  );

  return (
    <div className="visit-email-action-standalone content-area p-4">
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage} Redirecting to dashboard…
        </div>
      )}
      <Modal
        isOpen={modalOpen}
        onClose={handleClose}
        title={isFollowUp ? (isApprove ? 'Approve Follow-Up Letter' : 'Reject Follow-Up Letter') : (isApprove ? 'Approve Visit' : 'Reject Visit')}
        size="medium"
        footer={footer}
        showCloseButton={true}
        overlayClassName="electronic-sign-overlay"
      >
        <div className="electronic-sign-body">
          
          <div className="form-group">
            <label className="required">Username</label>
            <input
              type="text"
              className={`form-control ${usernameError ? 'is-invalid' : ''}`}
              placeholder="Enter username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (usernameError) setUsernameError('');
              }}
              autoComplete="off"
            />
            {usernameError && (
              <div className="electronic-sign-field-error text-danger">{usernameError}</div>
            )}
          </div>
          <div className="form-group">
            <label className="required">Password</label>
            <input
              type="password"
              className={`form-control ${passwordError ? 'is-invalid' : ''}`}
              placeholder="Enter password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              autoComplete="off"
            />
            {passwordError && (
              <div className="electronic-sign-field-error text-danger">{passwordError}</div>
            )}
          </div>
          <div className="form-group">
            <label className="required">Remarks</label>
            <textarea
              className={`form-control ${remarkError ? 'is-invalid' : ''}`}
              rows={2}
              placeholder="Enter Remarks"
              value={remark}
              onChange={(e) => {
                setRemark(e.target.value);
                if (remarkError) setRemarkError('');
              }}
            />
            {remarkError && (
              <div className="electronic-sign-field-error text-danger">{remarkError}</div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
